import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Zap, Bell, Slack, Mail, MessageSquare } from "lucide-react";

// Compact, auto-cycling teaser of the /tour experience for the left half of the login page.
// Cycles through 3 highlight cards. Non-interactive; links out to the full 60s tour.

const HIGHLIGHTS = [
  { id: "triage", label: "Morning triage", duration: 4200 },
  { id: "commitments", label: "Chase commitments", duration: 4200 },
  { id: "reminder", label: "Natural-language reminders", duration: 4200 },
];

function UserBubble({ children }) {
  return (
    <div className="flex justify-end mb-2.5">
      <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-neutral-900 text-white text-[13px] px-3.5 py-2 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function AiBubble({ children }) {
  return (
    <div className="flex items-start gap-2 mb-2.5">
      <div className="h-6 w-6 rounded-md bg-gradient-to-br from-neutral-700 to-neutral-900 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Sparkles size={11} className="text-white" />
      </div>
      <div className="max-w-[88%] rounded-2xl rounded-tl-sm bg-neutral-100 text-neutral-900 text-[13px] px-3.5 py-2 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function ToolChip({ name }) {
  return (
    <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700 border border-neutral-200">
      <Zap size={9} /> tool · {name}
    </span>
  );
}

function TriageCard() {
  return (
    <div>
      <UserBubble>What needs my attention today?</UserBubble>
      <div className="flex items-center gap-2 mb-2 text-[10px] text-neutral-500 pl-8">
        <ToolChip name="find_events" />
        <ToolChip name="find_commitments" />
        <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> running</span>
      </div>
      <AiBubble>
        <div className="font-medium mb-1.5">Three things worth your attention today —</div>
        <ol className="space-y-1.5 text-[12.5px]">
          <li><span className="inline-block px-1.5 py-0.5 rounded text-[9px] bg-red-50 text-red-700 mr-1.5">URGENCY 5/5</span><span className="font-medium">Priya (Acme) needs staging env by Friday.</span></li>
          <li><span className="inline-block px-1.5 py-0.5 rounded text-[9px] bg-orange-50 text-orange-700 mr-1.5">URGENCY 4/5</span><span className="font-medium">David flagged QA slippage on Observability v3.</span></li>
          <li><span className="inline-block px-1.5 py-0.5 rounded text-[9px] bg-amber-50 text-amber-700 mr-1.5">STALE</span><span className="font-medium">Aisha silent 15 days — renewal risk.</span></li>
        </ol>
      </AiBubble>
    </div>
  );
}

function CommitmentsCard() {
  const rows = [
    { who: "Priya Sharma", what: "Send SOW addendum", due: "today", risk: "at_risk" },
    { who: "Marcus Chen", what: "Confirm postmortem draft", due: "in 2d", risk: "at_risk" },
    { who: "Aisha Patel", what: "Renewal response — 15d silent", due: "overdue", risk: "overdue" },
  ];
  return (
    <div>
      <UserBubble>Who&apos;s owing us stuff right now?</UserBubble>
      <AiBubble>
        <div className="mb-2 text-[12.5px]">12 open commitments. Top 3 that matter:</div>
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <motion.div
              key={r.who}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + i * 0.15 }}
              className="flex items-center gap-2 rounded-md border border-neutral-200 bg-white px-2 py-1.5"
            >
              <div className="h-6 w-6 rounded-full bg-gradient-to-br from-neutral-300 to-neutral-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                {r.who.split(" ").map((n) => n[0]).join("").slice(0, 2)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-medium text-neutral-900 truncate">{r.what}</div>
                <div className="text-[10px] text-neutral-500 truncate">{r.who}</div>
              </div>
              <div className={`text-[10px] font-mono px-1.5 py-0.5 rounded whitespace-nowrap ${
                r.risk === "overdue" ? "bg-red-50 text-red-700"
                  : r.risk === "at_risk" ? "bg-amber-50 text-amber-700"
                  : "bg-neutral-100 text-neutral-700"
              }`}>{r.due}</div>
            </motion.div>
          ))}
        </div>
      </AiBubble>
    </div>
  );
}

function ReminderCard() {
  return (
    <div>
      <UserBubble>remind me to chase Legal for DPA in 3 hours</UserBubble>
      <div className="flex items-center gap-2 mb-2 text-[10px] text-neutral-500 pl-8">
        <ToolChip name="create_reminder" />
        <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> writing</span>
      </div>
      <AiBubble>
        <div className="text-[12.5px]">Done. You&apos;ll be pinged in 3 hours.</div>
      </AiBubble>
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="ml-8 mt-1 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5"
      >
        <div className="h-6 w-6 rounded-md bg-emerald-500 text-white flex items-center justify-center">
          <Bell size={11} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-medium text-emerald-900">Ping Legal for DPA status</div>
          <div className="text-[10px] text-emerald-700">If not kicked off, escalate to Wei · fires in 3h</div>
        </div>
        <div className="text-[10px] font-mono text-emerald-700">✓</div>
      </motion.div>
    </div>
  );
}

const CARD_COMPONENTS = {
  triage: TriageCard,
  commitments: CommitmentsCard,
  reminder: ReminderCard,
};

export default function TourPreview() {
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(0);
    const active = HIGHLIGHTS[idx];
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / active.duration);
      setProgress(p);
      if (p >= 1) {
        setIdx((i) => (i + 1) % HIGHLIGHTS.length);
      } else {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [idx]);

  const active = HIGHLIGHTS[idx];
  const CardComp = CARD_COMPONENTS[active.id];

  return (
    <div className="w-full max-w-lg mx-auto flex flex-col" data-testid="login-tour-preview">
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-3">
        Demo · Quick tour
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 leading-[1.1] mb-3">
        Run your founder&apos;s office <span className="text-red-600">on autopilot.</span>
      </h2>
      <p className="text-sm text-neutral-600 leading-relaxed mb-5 max-w-md">
        AI copilot, hierarchical projects, cross-channel triage — all in one calm room.
      </p>

      {/* Card viewport */}
      <div className="relative rounded-xl border border-neutral-200 bg-white shadow-lg overflow-hidden min-h-[280px]">
        <div className="px-3.5 py-2 border-b border-neutral-200 flex items-center gap-2 text-[11px] text-neutral-500">
          <Sparkles size={11} /> Ask anything · {active.label}
        </div>
        <div className="p-3.5">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <CardComp />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Segmented progress */}
      <div className="mt-4 flex items-center gap-1.5" data-testid="tour-preview-progress">
        {HIGHLIGHTS.map((c, i) => {
          const p = i < idx ? 1 : i === idx ? progress : 0;
          return (
            <div key={c.id} className="flex-1 h-1 rounded-full bg-neutral-200 overflow-hidden">
              <div
                className="h-full bg-neutral-900 transition-[width] duration-100 ease-linear"
                style={{ width: `${p * 100}%` }}
              />
            </div>
          );
        })}
      </div>

      {/* Channel chips */}
      <div className="mt-5 flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-mono uppercase text-neutral-500 mr-1">ingests</span>
        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-700"><Slack size={10} /> Slack</span>
        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-700"><Mail size={10} /> Gmail</span>
        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-700"><MessageSquare size={10} /> WhatsApp</span>
        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-700">Notion</span>
      </div>
    </div>
  );
}
